import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useI18n } from "../i18n/I18nProvider";

// Copiata dalla palette fAInance (core.tsx): ordine e valori identici.
export const MUNDELY_PRESET_COLORS = ["#124848","#093232","#6B9F8B","#F4C16A","#EDB253","#F7F5EF","#FFFFFF","#E5EFE9","#FFF1D6","#183B3A","#526964","#D8E2DC","#0E201F","#183330","#246747"];

function clamp(value: number, min: number, max: number) { return Math.max(min, Math.min(max, value)); }
function clampColorValue(value: number, min: number, max: number) { return Math.max(min, Math.min(max, Number(value) || 0)); }
function normalizeHtmlColor(value: string | undefined | null) {
  const raw = String(value || "").trim();
  if (/^#[0-9a-fA-F]{6}$/.test(raw)) return raw.toUpperCase();
  if (/^#[0-9a-fA-F]{3}$/.test(raw)) return ("#" + raw.slice(1).split("").map((char) => char + char).join("")).toUpperCase();
  return "";
}
function hexToRgbColor(value: string) {
  const hex = normalizeHtmlColor(value) || "#124848";
  return { r: parseInt(hex.slice(1, 3), 16), g: parseInt(hex.slice(3, 5), 16), b: parseInt(hex.slice(5, 7), 16) };
}
function rgbToHexColor(r: number, g: number, b: number) {
  const toHex = (v: number) => Math.round(clampColorValue(v, 0, 255)).toString(16).padStart(2, "0");
  return ("#" + toHex(r) + toHex(g) + toHex(b)).toUpperCase();
}
function rgbToHsvColor(r: number, g: number, b: number) {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b), delta = max - min;
  let h = 0;
  if (delta) {
    if (max === r) h = 60 * (((g - b) / delta) % 6);
    else if (max === g) h = 60 * ((b - r) / delta + 2);
    else h = 60 * ((r - g) / delta + 4);
  }
  if (h < 0) h += 360;
  return { h, s: max === 0 ? 0 : (delta / max) * 100, v: max * 100 };
}
function hexToHsvColor(value: string) {
  const rgb = hexToRgbColor(value);
  return rgbToHsvColor(rgb.r, rgb.g, rgb.b);
}
function hsvToHexColor(h: number, s: number, v: number) {
  h = (((Number(h) || 0) % 360) + 360) % 360;
  s = clampColorValue(s, 0, 100) / 100;
  v = clampColorValue(v, 0, 100) / 100;
  const c = v * s, x = c * (1 - Math.abs(((h / 60) % 2) - 1)), m = v - c;
  let r = 0, g = 0, b = 0;
  if (h < 60) { r = c; g = x; }
  else if (h < 120) { r = x; g = c; }
  else if (h < 180) { g = c; b = x; }
  else if (h < 240) { g = x; b = c; }
  else if (h < 300) { r = x; b = c; }
  else { r = c; b = x; }
  return rgbToHexColor((r + m) * 255, (g + m) * 255, (b + m) * 255);
}

function useDarkTheme() {
  const [dark, setDark] = useState(() => typeof document !== "undefined" && document.documentElement.dataset.tripTheme === "dark");
  useEffect(() => {
    if (typeof document === "undefined") return;
    const observer = new MutationObserver(() => setDark(document.documentElement.dataset.tripTheme === "dark"));
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["data-trip-theme"] });
    return () => observer.disconnect();
  }, []);
  return dark;
}

function FainanceToggle({ checked, onChange }: { checked: boolean; onChange: () => void }) {
  const dark = useDarkTheme();
  const color = "#124848";
  return <button type="button" aria-pressed={checked} onClick={onChange} style={{ display:"flex", alignItems:"center", justifyContent:"center", minWidth:56, minHeight:40, padding:6, margin:-6, border:"none", background:"transparent", cursor:"pointer", userSelect:"none", touchAction:"manipulation", WebkitTapHighlightColor:"transparent" }}>
    <div style={{ width:46, height:26, borderRadius:13, background:checked ? color : (dark ? "#526964" : "#D8E2DC"), position:"relative", transition:"background 0.2s", flexShrink:0 }}>
      <div style={{ position:"absolute", top:3, left:checked ? 23 : 3, width:20, height:20, borderRadius:"50%", background:"#fff", transition:"left 0.2s" }} />
    </div>
  </button>;
}

function FainanceColorPickerModal({ open, value, onChange, onClose }: { open: boolean; value: string; onChange: (value: string) => void; onClose: () => void }) {
  const { t } = useI18n();
  const dark = useDarkTheme();
  const primary = "#124848";
  const initial = normalizeHtmlColor(value) || "#124848";
  const initialHsv = hexToHsvColor(initial);
  const [draft, setDraft] = useState(initial);
  const [html, setHtml] = useState(initial);
  const [hue, setHue] = useState(initialHsv.h);
  const [saturation, setSaturation] = useState(initialHsv.s);
  const [brightness, setBrightness] = useState(initialHsv.v);
  const [mode, setMode] = useState<"preset" | "custom">("preset");
  const svRef = useRef<HTMLDivElement | null>(null);
  const hueRef = useRef<HTMLDivElement | null>(null);
  const dragRef = useRef<"" | "sv" | "hue">("");

  useEffect(() => {
    if (!open) return;
    const next = normalizeHtmlColor(value) || "#124848";
    const hsv = hexToHsvColor(next);
    setDraft(next); setHtml(next); setHue(hsv.h); setSaturation(hsv.s); setBrightness(hsv.v);
    setMode("preset");
    dragRef.current = "";
  }, [open, value]);

  if (!open || typeof document === "undefined") return null;
  const valid = normalizeHtmlColor(html);
  const border = dark ? "#526964" : "#D8E2DC";
  const panel = dark ? "#183B3A" : "#FFFFFF";
  const text = dark ? "#FFFFFF" : "#183B3A";
  const sub = dark ? "#D8E2DC" : "#526964";

  function chooseDefault(color: string) { onChange(color); onClose(); }
  function applyCustom() { if (!valid) return; onChange(valid); onClose(); }
  function setFromHsv(nextHue: number, nextSaturation: number, nextBrightness: number) {
    const h = clampColorValue(nextHue, 0, 360), s = clampColorValue(nextSaturation, 0, 100), v = clampColorValue(nextBrightness, 0, 100);
    const color = hsvToHexColor(h, s, v);
    setHue(h); setSaturation(s); setBrightness(v); setDraft(color); setHtml(color);
  }
  function updateSaturationBrightness(event: React.PointerEvent<HTMLDivElement>) {
    const element = svRef.current; if (!element) return;
    const rect = element.getBoundingClientRect();
    const x = clampColorValue(event.clientX - rect.left, 0, rect.width), y = clampColorValue(event.clientY - rect.top, 0, rect.height);
    setFromHsv(hue, (x / Math.max(1, rect.width)) * 100, 100 - (y / Math.max(1, rect.height)) * 100);
  }
  function updateHue(event: React.PointerEvent<HTMLDivElement>) {
    const element = hueRef.current; if (!element) return;
    const rect = element.getBoundingClientRect();
    const x = clampColorValue(event.clientX - rect.left, 0, rect.width);
    setFromHsv((x / Math.max(1, rect.width)) * 360, saturation, brightness);
  }
  function startDrag(kind: "sv" | "hue", event: React.PointerEvent<HTMLDivElement>) {
    dragRef.current = kind;
    try { event.currentTarget.setPointerCapture(event.pointerId); } catch {}
    if (kind === "sv") updateSaturationBrightness(event); else updateHue(event);
  }
  function continueDrag(kind: "sv" | "hue", event: React.PointerEvent<HTMLDivElement>) {
    if (dragRef.current !== kind) return;
    if (kind === "sv") updateSaturationBrightness(event); else updateHue(event);
  }
  function changeHtml(raw: string) {
    setHtml(raw);
    const color = normalizeHtmlColor(raw);
    if (!color) return;
    const hsv = hexToHsvColor(color); setDraft(color); setHue(hsv.h); setSaturation(hsv.s); setBrightness(hsv.v);
  }

  return createPortal(<div role="dialog" aria-modal="true" onClick={(event) => { if (event.target === event.currentTarget) onClose(); }} style={{ position:"fixed", inset:0, zIndex:10200, background:"rgba(20,20,30,0.66)", backdropFilter:"blur(5px)", display:"flex", alignItems:"flex-start", justifyContent:"center", padding:"max(82px,calc(env(safe-area-inset-top,0px) + 72px)) clamp(8px,2.5vw,16px) 16px", boxSizing:"border-box", overflowY:"auto", WebkitOverflowScrolling:"touch" }}>
    <div onClick={(event) => event.stopPropagation()} style={{ width:"min(620px,100%)", maxHeight:"calc(100dvh - 98px)", display:"flex", flexDirection:"column", overflow:"hidden", background:dark ? "#093232" : "#fff", border:"1px solid " + (dark ? "#526964" : "#D8E2DC"), borderRadius:24, boxShadow:"0 24px 80px rgba(20,20,30,0.34)" }}>
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:10, padding:"14px clamp(12px,3.8vw,18px)", background:dark ? "#093232" : "#FFFFFF", borderBottom:"1px solid " + (dark ? "#526964" : "#D8E2DC") }}>
        <div style={{ fontSize:"clamp(16.8px,4.8vw,18.8px)", fontWeight:950, color:text, lineHeight:1.15 }}>{t("widgets.colorPicker.title")}</div>
        <button type="button" onClick={onClose} aria-label={t("common.close")} style={{ width:38, height:38, borderRadius:12, border:"1px solid #A83D48", background:"#A83D48", color:"#fff", fontSize:23, lineHeight:1, cursor:"pointer" }}>×</button>
      </div>
      <div style={{ padding:"clamp(11px,3.6vw,18px)", overflowY:"auto", WebkitOverflowScrolling:"touch" }}>
        <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(2,minmax(0,1fr))", border:"1px solid " + border, borderRadius:12, overflow:"hidden", background:dark ? "#093232" : "#E5EFE9" }}>
            <button type="button" onClick={() => setMode("preset")} style={{ border:"none", borderBottom:mode === "preset" ? "3px solid " + primary : "3px solid transparent", background:mode === "preset" ? (dark ? "#183B3A" : "#fff") : "transparent", color:mode === "preset" ? primary : text, padding:"12px 8px", fontSize:14, fontWeight:mode === "preset" ? 900 : 750, cursor:"pointer" }}>{t("widgets.colorPicker.presets")}</button>
            <button type="button" onClick={() => setMode("custom")} style={{ border:"none", borderBottom:mode === "custom" ? "3px solid " + primary : "3px solid transparent", background:mode === "custom" ? (dark ? "#183B3A" : "#fff") : "transparent", color:mode === "custom" ? primary : text, padding:"12px 8px", fontSize:14, fontWeight:mode === "custom" ? 900 : 750, cursor:"pointer" }}>{t("widgets.colorPicker.custom")}</button>
          </div>
          {mode === "preset" && <div style={{ background:panel, border:"1px solid " + border, borderRadius:16, padding:14 }}>
            <div style={{ fontSize:12, fontWeight:900, color:text, marginBottom:11 }}>{t("widgets.colorPicker.presetColors")}</div>
            <div style={{ display:"grid", gridTemplateColumns:"repeat(5,minmax(42px,1fr))", gap:10 }}>
              {MUNDELY_PRESET_COLORS.map((color) => { const active = normalizeHtmlColor(value) === normalizeHtmlColor(color); return <button type="button" key={color} aria-label={color} onClick={() => chooseDefault(color)} style={{ height:48, borderRadius:13, border:active ? "3px solid " + (dark ? "#fff" : "#183B3A") : "3px solid transparent", background:color, cursor:"pointer", boxShadow:active ? "0 0 0 3px " + color + "35" : "none", display:"flex", alignItems:"center", justifyContent:"center", color:"#fff", fontSize:18, fontWeight:950, textShadow:"0 1px 4px rgba(0,0,0,.28)" }}>{active ? "✓" : ""}</button>; })}
            </div>
          </div>}
          {mode === "custom" && <div style={{ background:panel, border:"1px solid " + border, borderRadius:16, padding:14 }}>
            <div style={{ fontSize:12, fontWeight:900, color:text, marginBottom:11 }}>{t("widgets.colorPicker.customColor")}</div>
            <div ref={svRef} role="slider" aria-label={t("widgets.colorPicker.customColor")} tabIndex={0} onPointerDown={(event) => startDrag("sv", event)} onPointerMove={(event) => continueDrag("sv", event)} onPointerUp={() => { dragRef.current = ""; }} onPointerCancel={() => { dragRef.current = ""; }} style={{ position:"relative", height:190, borderRadius:15, overflow:"hidden", cursor:"crosshair", touchAction:"none", background:`linear-gradient(to top,#183B3A,transparent),linear-gradient(to right,#fff,hsl(${hue},100%,50%))`, border:"1px solid " + border, boxShadow:dark ? "inset 0 0 0 1px rgba(255,255,255,.04)" : "inset 0 0 0 1px rgba(0,0,0,.03)" }}>
              <span style={{ position:"absolute", left:saturation + "%", top:(100 - brightness) + "%", width:22, height:22, borderRadius:"50%", border:"3px solid #fff", boxShadow:"0 1px 8px rgba(0,0,0,.75)", transform:"translate(-50%,-50%)", background:draft, pointerEvents:"none", boxSizing:"border-box" }} />
            </div>
            <div ref={hueRef} role="slider" aria-label={t("widgets.colorPicker.color")} tabIndex={0} onPointerDown={(event) => startDrag("hue", event)} onPointerMove={(event) => continueDrag("hue", event)} onPointerUp={() => { dragRef.current = ""; }} onPointerCancel={() => { dragRef.current = ""; }} style={{ position:"relative", height:30, borderRadius:999, marginTop:13, cursor:"pointer", touchAction:"none", background:"linear-gradient(to right,#A83D48 0%,#F4C16A 16.66%,#6B9F8B 33.33%,#124848 50%,#124848 66.66%,#124848 83.33%,#A83D48 100%)", border:"1px solid " + border }}>
              <span style={{ position:"absolute", left:(hue / 360) * 100 + "%", top:"50%", width:24, height:36, borderRadius:10, border:"3px solid #fff", boxShadow:"0 2px 8px rgba(0,0,0,.5)", transform:"translate(-50%,-50%)", background:`hsl(${hue},100%,50%)`, pointerEvents:"none", boxSizing:"border-box" }} />
            </div>
            <div style={{ display:"grid", gridTemplateColumns:"64px minmax(0,1fr)", gap:12, alignItems:"end", marginTop:14 }}>
              <div style={{ width:64, height:46, borderRadius:12, background:draft, border:"2px solid " + (dark ? "rgba(255,255,255,.3)" : "rgba(47,49,66,.16)"), boxShadow:"0 4px 14px " + draft + "35", boxSizing:"border-box" }} />
              <div><label style={{ fontSize:11, fontWeight:800, color:sub, display:"block", marginBottom:6 }}>{t("widgets.colorPicker.htmlCode")}</label><input value={html} onChange={(event) => changeHtml(event.target.value)} placeholder={t("widgets.colorPicker.htmlPlaceholder")} style={{ width:"100%", height:46, boxSizing:"border-box", border:"1px solid " + (html && !valid ? "#A83D48" : border), borderRadius:12, padding:"0 12px", fontSize:15, fontWeight:850, letterSpacing:.4, background:dark ? "#093232" : "#fff", color:text, outline:"none" }} /></div>
            </div>
            {html && !valid && <div style={{ fontSize:11, color:"#A83D48", fontWeight:800, marginTop:7 }}>{t("widgets.colorPicker.invalidHtml")}</div>}
          </div>}
          {mode === "custom" && <div style={{ display:"grid", gridTemplateColumns:"repeat(2,minmax(0,1fr))", gap:10 }}>
            <button type="button" disabled={!valid} onClick={applyCustom} style={{ border:"none", borderRadius:12, padding:"12px 14px", background:valid ? primary : "#526964", color:"#fff", fontSize:14, fontWeight:900, cursor:valid ? "pointer" : "not-allowed" }}>{t("widgets.colorPicker.apply")}</button>
            <button type="button" onClick={onClose} style={{ border:"1px solid " + border, borderRadius:12, padding:"12px 14px", background:dark ? "#093232" : "#E5EFE9", color:text, fontSize:14, fontWeight:850, cursor:"pointer" }}>{t("common.cancel")}</button>
          </div>}
        </div>
      </div>
    </div>
  </div>, document.body);
}

function FainanceColorSelector({ value, onChange, expanded = false }: { value: string; onChange: (value: string) => void; expanded?: boolean }) {
  const { t } = useI18n();
  const dark = useDarkTheme();
  const [open, setOpen] = useState(false);
  const selected = normalizeHtmlColor(value) || "#124848";
  const border = dark ? "#526964" : "#D8E2DC";
  const panel = dark ? "#183B3A" : "#fff";
  return <><button type="button" onClick={() => setOpen(true)} aria-label={t("widgets.colorPicker.color")}
    style={{ width:expanded ? "100%" : 42, minWidth:expanded ? 0 : 42, height:expanded ? 54 : 38,
    display:"flex", alignItems:"center", justifyContent:expanded ? "flex-start" : "center", gap:10,
    background:panel, border:"1px solid " + border, borderRadius:expanded ? 12 : 10,
    padding:expanded ? "7px 10px" : 4, cursor:"pointer", boxSizing:"border-box", flexShrink:0 }}>
    <span style={{ width:expanded ? 36 : 28, height:expanded ? 36 : 28,
      borderRadius:expanded ? 11 : 8, background:selected,
      border:"2px solid " + (dark ? "rgba(255,255,255,.34)" : "rgba(47,49,66,.18)"),
      boxShadow:"0 3px 10px " + selected + "35", flexShrink:0 }} />
    {expanded && <><span style={{flex:1}}/><span aria-hidden="true" style={{fontSize:22,color:dark ? "#B4C9BD" : "#526964",fontWeight:850}}>›</span></>}
  </button><FainanceColorPickerModal open={open} value={selected} onChange={onChange} onClose={() => setOpen(false)} /></>;
}


export { FainanceColorSelector, FainanceToggle, useDarkTheme };
